import { useState } from 'react';
import { Loader2, Key, Globe, Smartphone } from 'lucide-react';
import { api } from '../lib/api';

interface LoginScreenProps {
  onLogin: (token: string) => void;
}

type LoginMode = 'password' | 'token';

export default function LoginScreen({ onLogin }: LoginScreenProps) {
  const [mode, setMode] = useState<LoginMode>('password');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Password login state
  const [telegramId, setTelegramId] = useState('');
  const [password, setPassword] = useState('');

  // Token login state
  const [token, setToken] = useState('');

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!telegramId.trim() || !password.trim()) {
      setError('Please enter both Telegram ID and password');
      return;
    }

    const id = parseInt(telegramId.trim(), 10);
    if (isNaN(id)) {
      setError('Telegram ID must be a number');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const result = await api.loginWithPassword(id, password.trim());
      if (result.success && result.token) {
        onLogin(result.token);
      } else {
        setError(result.error || 'Invalid credentials');
      }
    } catch (err) {
      setError('Login failed. Please try again.');
    }

    setIsLoading(false);
  };

  const handleTokenSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token.trim()) {
      setError('Please enter a token');
      return;
    }

    setError('');
    setIsLoading(true);

    try {
      const result = await api.validateToken(token.trim());
      if (result.valid && result.user) {
        onLogin(token.trim());
      } else {
        setError(result.error || 'Invalid token');
      }
    } catch (err) {
      setError('Token validation failed. Please try again.');
    }

    setIsLoading(false);
  };

  // Detect platform
  const isTMA = !!(window as any).Telegram?.WebApp?.initData;

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-6">
      <div className="w-full max-w-sm">
        {/* Logo - Minimalist black & white */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gradient-to-br from-white/10 to-white/5 flex items-center justify-center border border-white/10">
            <span className="text-2xl font-bold text-white">N</span>
          </div>
          <h1 className="text-2xl font-semibold text-white">Neron</h1>
          <p className="text-sm text-gray-500 mt-1">Knowledge Graph</p>
        </div>

        {/* Platform indicator */}
        <div className="flex items-center justify-center gap-2 text-xs text-gray-500 mb-6">
          {isTMA ? (
            <>
              <Smartphone size={14} />
              <span>Telegram Mini App</span>
            </>
          ) : (
            <>
              <Globe size={14} />
              <span>Web Browser</span>
            </>
          )}
        </div>

        {/* Mode switcher */}
        <div className="flex gap-2 mb-6">
          <button
            type="button"
            onClick={() => { setMode('password'); setError(''); }}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              mode === 'password'
                ? 'bg-white text-black'
                : 'bg-gray-900 text-gray-400 hover:text-white'
            }`}
          >
            Password
          </button>
          <button
            type="button"
            onClick={() => { setMode('token'); setError(''); }}
            className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-colors ${
              mode === 'token'
                ? 'bg-white text-black'
                : 'bg-gray-900 text-gray-400 hover:text-white'
            }`}
          >
            Token
          </button>
        </div>

        {/* Error display */}
        {error && (
          <div className="px-4 py-3 mb-4 bg-red-900/20 border border-red-900/50 rounded-lg text-red-400 text-sm">
            {error}
          </div>
        )}

        {/* Password Login Form */}
        {mode === 'password' && (
          <form onSubmit={handlePasswordSubmit} className="space-y-4">
            <div>
              <label htmlFor="telegramId" className="block text-sm text-gray-400 mb-2">
                Telegram ID
              </label>
              <input
                id="telegramId"
                type="text"
                inputMode="numeric"
                value={telegramId}
                onChange={(e) => setTelegramId(e.target.value.replace(/\D/g, ''))}
                placeholder="Your Telegram user ID"
                className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-gray-700 transition-colors"
                autoComplete="username"
                autoFocus
              />
              <p className="text-xs text-gray-600 mt-1">
                Get your ID from @userinfobot
              </p>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm text-gray-400 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Your webapp password"
                className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-gray-700 transition-colors"
                autoComplete="current-password"
              />
              <p className="text-xs text-gray-600 mt-1">
                Use /password in the bot to get your password
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading || !telegramId.trim() || !password.trim()}
              className="w-full py-3 bg-white text-black font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Signing in...
                </>
              ) : (
                <>
                  <Key size={18} />
                  Sign In
                </>
              )}
            </button>
          </form>
        )}

        {/* Token Login Form */}
        {mode === 'token' && (
          <form onSubmit={handleTokenSubmit} className="space-y-4">
            <div>
              <label htmlFor="token" className="block text-sm text-gray-400 mb-2">
                Access Token
              </label>
              <input
                id="token"
                type="password"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Paste token from /webapp command..."
                className="w-full px-4 py-3 bg-gray-900 border border-gray-800 rounded-lg text-white placeholder-gray-600 focus:outline-none focus:border-gray-700 transition-colors"
                autoComplete="off"
                autoFocus
              />
              <p className="text-xs text-gray-600 mt-1">
                Use /webapp in the bot to get a token
              </p>
            </div>

            <button
              type="submit"
              disabled={isLoading || !token.trim()}
              className="w-full py-3 bg-white text-black font-medium rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 size={18} className="animate-spin" />
                  Validating...
                </>
              ) : (
                'Connect with Token'
              )}
            </button>
          </form>
        )}

        {/* Hint */}
        <div className="mt-8 pt-6 border-t border-gray-800">
          <p className="text-center text-xs text-gray-500">
            Your Telegram ID: <span className="text-gray-400">1890816031</span>
          </p>
        </div>
      </div>
    </div>
  );
}
